import { useState, useRef } from 'react'
import { useGlobalProgressStore } from '../../stores/globalProgressStore'
import { getValidAuthToken } from '../../services/api'

interface MultiImageUploaderProps {
  productId: string
  existingImages?: Array<{
    id: string
    image_url: string
    is_primary: boolean
    sort_order: number
  }>
  onImagesChange?: (images: any[]) => void
  maxImages?: number
}

export default function MultiImageUploader({
  productId,
  existingImages = [],
  onImagesChange,
  maxImages = 4
}: MultiImageUploaderProps) {
  const [images, setImages] = useState(existingImages)
  const [uploading, setUploading] = useState(false)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { setGlobalProgress } = useGlobalProgressStore()

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    // Check if we can add more images
    const remainingSlots = maxImages - images.length
    if (remainingSlots <= 0) {
      alert(`Maximum ${maxImages} images allowed`)
      return
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots)

    // Validate file types and sizes
    for (const file of filesToUpload) {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`)
        return
      }
      if (file.size > 25 * 1024 * 1024) {
        alert(`${file.name} is too large. Max size is 25MB`)
        return
      }
    }

    setUploading(true)
    setGlobalProgress({ isActive: true, message: 'Uploading images...', progress: 0 })

    try {
      const formData = new FormData()
      filesToUpload.forEach(file => {
        formData.append('images', file)
      })

      // Get auth token
      const token = await getValidAuthToken()
      if (!token) {
        alert('Not authenticated')
        return
      }

      // Upload images
      const API_BASE_URL = import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/v1`
        : 'https://api.shop.bg502.ru/api/v1'

      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100)
          setGlobalProgress({ isActive: true, message: 'Uploading images...', progress })
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText)
            const newImages = [...images, ...result.images]
            setImages(newImages)
            onImagesChange?.(newImages)
            setGlobalProgress({ isActive: false })
          } catch (err) {
            console.error('Failed to parse response:', err)
            alert('Upload failed: Invalid response')
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText)
            alert(error.message || 'Upload failed')
          } catch {
            alert(`Upload failed: ${xhr.status}`)
          }
        }
        setUploading(false)
        setGlobalProgress({ isActive: false })
      })

      xhr.addEventListener('error', () => {
        alert('Network error during upload')
        setUploading(false)
        setGlobalProgress({ isActive: false })
      })

      xhr.open('POST', `${API_BASE_URL}/admin/products/${productId}/images`)
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.send(formData)
    } catch (error) {
      console.error('Upload error:', error)
      alert('Failed to upload images')
      setUploading(false)
      setGlobalProgress({ isActive: false })
    }

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDelete = async (imageId: string) => {
    if (!confirm('Delete this image?')) return

    try {
      const token = await getValidAuthToken()
      if (!token) {
        alert('Not authenticated')
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/v1`
        : 'https://api.shop.bg502.ru/api/v1'

      const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to delete')
      }

      const newImages = images.filter(img => img.id !== imageId)
      setImages(newImages)
      onImagesChange?.(newImages)
    } catch (error) {
      console.error('Delete error:', error)
      alert('Failed to delete image')
    }
  }

  const handleSetPrimary = async (imageId: string) => {
    try {
      const token = await getValidAuthToken()
      if (!token) {
        alert('Not authenticated')
        return
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/v1`
        : 'https://api.shop.bg502.ru/api/v1'

      const response = await fetch(`${API_BASE_URL}/admin/products/${productId}/images/${imageId}/primary`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to set primary')
      }

      const newImages = images.map(img => ({
        ...img,
        is_primary: img.id === imageId
      }))
      setImages(newImages)
      onImagesChange?.(newImages)
    } catch (error) {
      console.error('Set primary error:', error)
      alert('Failed to set primary image')
    }
  }

  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === dropIndex) return

    const newImages = [...images]
    const draggedImage = newImages[draggedIndex]
    newImages.splice(draggedIndex, 1)
    newImages.splice(dropIndex, 0, draggedImage)

    // Update sort order
    const updatedImages = newImages.map((img, idx) => ({
      ...img,
      sort_order: idx
    }))

    setImages(updatedImages)
    onImagesChange?.(updatedImages)
    setDraggedIndex(null)

    // Send reorder request
    try {
      const token = await getValidAuthToken()
      if (!token) return

      const API_BASE_URL = import.meta.env.VITE_API_URL
        ? `${import.meta.env.VITE_API_URL}/api/v1`
        : 'https://api.shop.bg502.ru/api/v1'

      await fetch(`${API_BASE_URL}/admin/products/${productId}/images/reorder`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_ids: updatedImages.map(img => img.id)
        })
      })
    } catch (error) {
      console.error('Reorder error:', error)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          Product Images ({images.length}/{maxImages})
        </label>
        {images.length < maxImages && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-sm bg-blue-500 text-white px-3 py-1 rounded-lg disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Add Images'}
          </button>
        )}
      </div>

      {/* Image Grid */}
      <div className="grid grid-cols-2 gap-4">
        {images.map((image, index) => (
          <div
            key={image.id}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            className="relative group border-2 border-gray-200 rounded-lg overflow-hidden cursor-move"
          >
            <img
              src={image.image_url}
              alt={`Product image ${index + 1}`}
              className="w-full h-32 object-cover"
            />

            {/* Primary Badge */}
            {image.is_primary && (
              <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                Primary
              </div>
            )}

            {/* Actions Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-2">
              {!image.is_primary && (
                <button
                  type="button"
                  onClick={() => handleSetPrimary(image.id)}
                  className="bg-green-500 text-white px-2 py-1 rounded text-xs"
                >
                  Set Primary
                </button>
              )}
              <button
                type="button"
                onClick={() => handleDelete(image.id)}
                className="bg-red-500 text-white px-2 py-1 rounded text-xs"
              >
                Delete
              </button>
            </div>
          </div>
        ))}

        {/* Add More Placeholder */}
        {images.length < maxImages && !uploading && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-lg h-32 flex flex-col items-center justify-center hover:border-gray-400 transition-colors"
          >
            <div className="text-3xl mb-2">➕</div>
            <div className="text-sm text-gray-500">Add Image</div>
            <div className="text-xs text-gray-400">Max 25MB</div>
          </button>
        )}

        {/* Uploading Placeholder */}
        {uploading && (
          <div className="border-2 border-blue-300 rounded-lg h-32 flex items-center justify-center bg-blue-50">
            <div className="text-sm text-blue-600">Uploading...</div>
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        Drag images to reorder. First image or primary image will be the main product image.
        Supports JPG, PNG, WebP, GIF (max 25MB each, max {maxImages} images total).
      </p>
    </div>
  )
}